import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { CalendarClock, CheckCircle2, FileCheck2, MessageSquareText, RefreshCw } from 'lucide-react'
import {
  ApiError,
  confirmOwnerWeek,
  fetchMyAlerts,
  fetchOpportunities,
  respondAlert,
} from '@/api/client'
import type { AlertMine, AlertRespondRequest } from '@/api/types'
import { isFinanceUser, isOwnerUser } from '@/api/types'
import { useAuth } from '@/auth/useAuth'
import { ControlRow } from '@/components/ControlRow'
import { DataTable, TableBody, TableHeader } from '@/components/DataTable'
import { DateField } from '@/components/DateField'
import { Drawer } from '@/components/Drawer'
import { EmptyState } from '@/components/EmptyState'
import { ErrorState } from '@/components/ErrorState'
import { HubPage } from '@/components/HubPage'
import { IconButton } from '@/components/IconButton'
import { LoadingState } from '@/components/LoadingState'
import { PageHelp } from '@/components/PageHelp'
import { formatDateTime } from '@/lib/format-date'

type RespondMode = AlertRespondRequest['response']

export function AlertsPage() {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()

  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [respondMode, setRespondMode] = useState<RespondMode | null>(null)
  const [sowReference, setSowReference] = useState('')
  const [newStartDate, setNewStartDate] = useState('')
  const [updateNotes, setUpdateNotes] = useState('')
  const [formError, setFormError] = useState<string | null>(null)
  const [weekConfirmed, setWeekConfirmed] = useState(false)

  const alertsQuery = useQuery({
    queryKey: ['alerts', 'mine'],
    queryFn: fetchMyAlerts,
    enabled: Boolean(user) && !isFinanceUser(user),
  })

  const ownedOppsQuery = useQuery({
    queryKey: ['opportunities', 'mine-for-confirm'],
    queryFn: () => fetchOpportunities({ limit: 1 }),
    enabled: Boolean(user) && isOwnerUser(user),
  })

  const alerts = useMemo(() => alertsQuery.data ?? [], [alertsQuery.data])
  const startDateAlerts = useMemo(
    () => alerts.filter((a) => (a.rule_code ?? '') !== 'weekly_prompt'),
    [alerts],
  )
  const needsWeeklyConfirm = useMemo(() => {
    if (!isOwnerUser(user) || weekConfirmed) return false
    if (alerts.some((a) => a.rule_code === 'weekly_prompt')) return true
    return (ownedOppsQuery.data?.total ?? 0) > 0
  }, [alerts, ownedOppsQuery.data?.total, user, weekConfirmed])

  const selected = alerts.find((a) => a.id === selectedId) ?? null
  const drawerOpen = Boolean(selected && selected.rule_code !== 'weekly_prompt')

  useEffect(() => {
    const alertId = searchParams.get('alert')
    if (alertId) {
      setSelectedId(alertId)
      setSearchParams(
        (prev) => {
          const copy = new URLSearchParams(prev)
          copy.delete('alert')
          return copy
        },
        { replace: true },
      )
    }
  }, [searchParams, setSearchParams])

  const confirmWeekMutation = useMutation({
    mutationFn: confirmOwnerWeek,
    onSuccess: () => {
      setWeekConfirmed(true)
      void queryClient.invalidateQueries({ queryKey: ['alerts', 'mine'] })
    },
  })

  const respondMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: AlertRespondRequest }) =>
      respondAlert(id, payload),
    onSuccess: () => {
      setSelectedId(null)
      setRespondMode(null)
      setSowReference('')
      setNewStartDate('')
      setUpdateNotes('')
      setFormError(null)
      void queryClient.invalidateQueries({ queryKey: ['alerts', 'mine'] })
      void queryClient.invalidateQueries({ queryKey: ['opportunities'] })
    },
    onError: (error: unknown) => {
      setFormError(error instanceof ApiError ? error.message : 'Could not save response.')
    },
  })

  function openRespond(alert: AlertMine, mode: RespondMode) {
    setSelectedId(alert.id)
    setRespondMode(mode)
    setFormError(null)
  }

  function submitResponse() {
    if (!selected || !respondMode) return
    const payload: AlertRespondRequest = { response: respondMode }
    if (respondMode === 'sow_signed') {
      if (!sowReference.trim()) {
        setFormError('Enter the SOW reference.')
        return
      }
      payload.sow_reference = sowReference.trim()
    }
    if (respondMode === 'revise_start') {
      if (!newStartDate) {
        setFormError('Choose a new start date.')
        return
      }
      payload.new_start_date = newStartDate
    }
    if (respondMode === 'provide_update') {
      payload.update_notes = updateNotes.trim() || null
    }
    respondMutation.mutate({ id: selected.id, payload })
  }

  if (isFinanceUser(user)) {
    return (
      <section className="page page--scroll">
        <header className="page__header">
          <div className="page__header-lead">
            <div>
              <h1 className="page__title">Alerts</h1>
              <p className="page__subtitle">Owner alerts are not shown for Finance accounts.</p>
            </div>
            <PageHelp />
          </div>
        </header>
      </section>
    )
  }

  return (
    <>
      <HubPage
        chrome={
          <>
            <ControlRow
              sticky={false}
              leading={
                <>
                  <h1 className="page__title">Alerts</h1>
                  <p className="page__subtitle">
                    {alertsQuery.isLoading
                      ? 'Loading…'
                      : `${startDateAlerts.length} open · respond in place`}
                  </p>
                </>
              }
              actions={
                <IconButton
                  label="Refresh alerts"
                  variant="ghost"
                  onClick={() => void alertsQuery.refetch()}
                >
                  <RefreshCw size={18} strokeWidth={2} />
                </IconButton>
              }
            />

            {needsWeeklyConfirm && (
              <div className="banner banner--info" role="status">
                <CheckCircle2 size={20} strokeWidth={2} aria-hidden />
                <div className="banner__body">
                  <strong>Confirm this week&apos;s forecast</strong>
                  <span>
                    Due end of Tuesday (India time). One click confirms all your opportunities.
                  </span>
                </div>
                <button
                  type="button"
                  className="button button--primary"
                  disabled={confirmWeekMutation.isPending}
                  onClick={() => confirmWeekMutation.mutate()}
                >
                  Confirm week
                </button>
              </div>
            )}
          </>
        }
      >
        {alertsQuery.isError && (
          <ErrorState message="Could not load alerts." onRetry={() => void alertsQuery.refetch()} />
        )}

        {confirmWeekMutation.isError && (
          <ErrorState
            message={
              confirmWeekMutation.error instanceof ApiError
                ? confirmWeekMutation.error.message
                : 'Could not confirm week.'
            }
          />
        )}

        {alertsQuery.isLoading && <LoadingState variant="skeleton-table" columns={4} />}

        {!alertsQuery.isLoading && !alertsQuery.isError && alerts.length === 0 && (
          <EmptyState title="No open alerts — you are up to date." />
        )}

        {!alertsQuery.isLoading && alerts.length > 0 && (
          <DataTable dense fill>
            <TableHeader>
              <tr>
                <th scope="col">Opportunity</th>
                <th scope="col">Rule</th>
                <th scope="col">Sent</th>
                <th scope="col">
                  <span className="visually-hidden">Actions</span>
                </th>
              </tr>
            </TableHeader>
            <TableBody>
              {alerts.map((alert) => (
                <tr key={alert.id}>
                  <td>{alert.opportunity_name}</td>
                  <td>{alert.rule_name}</td>
                  <td>{formatDateTime(alert.sent_at)}</td>
                  <td className="table__actions">
                    {alert.rule_code === 'weekly_prompt' ? (
                      <span className="badge">Use Confirm week banner</span>
                    ) : (
                      <>
                        <IconButton
                          label="SOW signed"
                          variant="ghost"
                          onClick={() => openRespond(alert, 'sow_signed')}
                        >
                          <FileCheck2 size={18} strokeWidth={2} />
                        </IconButton>
                        <IconButton
                          label="Revise start date"
                          variant="ghost"
                          onClick={() => openRespond(alert, 'revise_start')}
                        >
                          <CalendarClock size={18} strokeWidth={2} />
                        </IconButton>
                        <IconButton
                          label="Provide update"
                          variant="ghost"
                          onClick={() => openRespond(alert, 'provide_update')}
                        >
                          <MessageSquareText size={18} strokeWidth={2} />
                        </IconButton>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </TableBody>
          </DataTable>
        )}
      </HubPage>

      <Drawer
        open={drawerOpen}
        title={selected ? `Respond · ${selected.opportunity_name}` : 'Respond'}
        onClose={() => {
          setSelectedId(null)
          setRespondMode(null)
          setFormError(null)
        }}
        footer={
          respondMode ? (
            <div className="drawer__footer-actions">
              <button
                type="button"
                className="button button--ghost"
                onClick={() => setRespondMode(null)}
              >
                Back
              </button>
              <button
                type="button"
                className="button button--primary"
                disabled={respondMutation.isPending}
                onClick={submitResponse}
              >
                Confirm response
              </button>
            </div>
          ) : null
        }
      >
        {selected && !respondMode && (
          <div className="alert-respond-actions">
            <button
              type="button"
              className="alert-respond-actions__item"
              onClick={() => setRespondMode('sow_signed')}
            >
              <FileCheck2 size={28} strokeWidth={1.75} aria-hidden />
              <span>SOW signed</span>
            </button>
            <button
              type="button"
              className="alert-respond-actions__item"
              onClick={() => setRespondMode('revise_start')}
            >
              <CalendarClock size={28} strokeWidth={1.75} aria-hidden />
              <span>Revise start</span>
            </button>
            <button
              type="button"
              className="alert-respond-actions__item"
              onClick={() => setRespondMode('provide_update')}
            >
              <MessageSquareText size={28} strokeWidth={1.75} aria-hidden />
              <span>Provide update</span>
            </button>
          </div>
        )}

        {respondMode === 'sow_signed' && (
          <label className="field">
            <span className="field__label">SOW reference</span>
            <input
              className="field__input"
              value={sowReference}
              onChange={(e) => setSowReference(e.target.value)}
              autoFocus
            />
          </label>
        )}

        {respondMode === 'revise_start' && (
          <DateField label="New start date" value={newStartDate} onChange={setNewStartDate} />
        )}

        {respondMode === 'provide_update' && (
          <label className="field">
            <span className="field__label">Update note</span>
            <textarea
              className="field__input field__input--textarea"
              rows={4}
              value={updateNotes}
              onChange={(e) => setUpdateNotes(e.target.value)}
              autoFocus
            />
          </label>
        )}

        {formError && (
          <p className="alert alert--error" role="alert">
            {formError}
          </p>
        )}
      </Drawer>
    </>
  )
}
